'use client';

import { usePathname } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';

import { handleLogRocket, handleSentry } from '@/helpers/logging';
import * as gtag from '@/lib/gtag';
import { CurrentUserType } from '@/queries/current-user';

export interface GlobalStateInterface {
    currentUser?: CurrentUserType | null;
    currentUserLoading: boolean;
}

const defaultGlobalState = {
    currentUser: undefined,
    currentUserLoading: true,
};

const GlobalStateContext = createContext({
    globalState: {} as Partial<GlobalStateInterface>,
    setGlobalState: {} as ReactStateSetter<Partial<GlobalStateInterface>>,
});

const GlobalStateProvider = ({
    children,
    value = { ...defaultGlobalState } as GlobalStateInterface,
}: {
    children: React.ReactNode;
    value?: Partial<GlobalStateInterface>;
}) => {
    const posthog = usePostHog();
    const pathname = usePathname();
    const [globalState, setGlobalState] = useState(value);
    const previousCurrentUser = usePrevious(value.currentUser);

    useEffect(() => {
        if (value.currentUser && previousCurrentUser?.id !== value.currentUser.id) {
            const user = value.currentUser;

            handleLogRocket(user, pathname as string);
            handleSentry(user);
            gtag.setUser(user.id);
            posthog.identify(user.email, {
                email: user.email,
                id: user.id,
                isThematicAdmin: user.isThematicAdmin,
                name: user.name,
            });
        }
    }, [pathname, posthog, previousCurrentUser?.id, value.currentUser]);

    return (
        <GlobalStateContext.Provider value={{ globalState, setGlobalState }}>{children}</GlobalStateContext.Provider>
    );
};

const useGlobalState = () => {
    const context = useContext(GlobalStateContext);
    if (!context) {
        throw new Error('useGlobalState must be used within a GlobalStateContext');
    }
    return context;
};

export { GlobalStateProvider, useGlobalState };
